import React, { useEffect, useState, useCallback } from 'react';
import { DataGridPro, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid-pro';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { ContentTitle } from '../ui/styled';
import { Auth } from 'aws-amplify';
import BackIcon from '../common/Back/BackIcon';
import PageHeading from '../common/Heading/PageHeading';
import CommonSearch from '../common/Search/CommonSearch';
import { commonQueryParams } from '../../services/QueryParams/QueryParams';
import { paginationSize, TREATMENT_LIST_URL } from '../../constants';
import { renderCellExpand } from '../common/GridCell/GridCellExpand';
import { getTreatmentList } from './services/treatmentsApi';

const TreatmentList = (props) => {
  const [user, setUser] = useState(null);
  const [searchTreatmentList, setSearchTreatmentList] = useState('');
  const [treatmentList, setTreatmentList] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
    sortModel: '',
    filterModel: {} 
  });

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchData();
    }
    getUser().then((userData) => setUser(userData));
  }, [
    user?.signInUserSession.accessToken.jwtToken,
    treatmentList.pageSize,
    treatmentList.page,
    treatmentList.sortModel,
    treatmentList.filterModel,
    searchTreatmentList,
  ]);

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  const QuickSearchToolbar = () => {
    return (
      <Box pt={1} sx={{ flexGrow: 1, backgroundColor: '#747070' }}>
        <Grid container spacing={1}>
          <Grid item xs={11}>
            <CommonSearch
              searchText={searchTreatmentList}
              setSearchText={setSearchTreatmentList}
            />
          </Grid>
          <Grid item xs={1}>  
            <GridToolbarContainer>
              <GridToolbarExport />
            </GridToolbarContainer>
          </Grid>
        </Grid>    
      </Box>  
    );
  };

  const fetchData = async () => {
    setTreatmentList((prveState) => ({ ...prveState, isLoading: true }));
    const commonParams = commonQueryParams(
      TREATMENT_LIST_URL,
      searchTreatmentList,
      treatmentList
    );
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    const treatments = await getTreatmentList({
      commonParams,
      authorization,
    });
    setTreatmentList((prveState) => ({
      ...prveState,
      ...treatments,
    }));
  };

  const handleSortModelChange = useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    setTreatmentList((old) => ({ ...old, sortModel: [...sortModel] }));
  }, []);

  const handleFilterChange = useCallback((filterModel) => {
    // Here you save the data you need from the filter model
    //console.log(filterModel);
    setTreatmentList((old) => ({ ...old, filterModel: {...filterModel} }));
  }, []);

  const columnsData = [
    {
      field: 'CDCNumber',
      headerName: 'CDC Number',
      renderCell: renderCellExpand,
      flex: 1,
      filterable: false
    },
    {
      field: 'epdID',
      headerName: 'Episode Id',
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'epdDate',
      headerName: 'Episode Date',
      type: 'date',
      minWidth: 80,
      flex: 1,
      filterable: false
    },
    {
      field: 'athDate',
      headerName: 'Auth Date',
      type: 'date',
      minWidth: 80,
      flex: 1,
      filterable: false
    },
    {
      field: 'sapAthID',
      headerName: 'Sap Auth Id',
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'CBPAthID',
      headerName: 'Cbp Auth Id',
      renderCell: renderCellExpand,
      flex: 1,
      filterable: false
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      type: 'date',
      minWidth: 80,
      flex: 1,
    },
    {
      field: 'cBPName',
      headerName: 'Cbp Name',
      minWidth: 120,
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'cbpID',
      headerName: 'CBP Id',
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'TmtID',
      headerName: 'Treatment Id',
      renderCell: renderCellExpand,
      flex: 1,
      filterable: false
    },
    { field: 'Date', headerName: 'Date', type: 'date', minWidth: 80, flex: 1 },
    {
      field: 'treatmentTypeId',
      headerName: 'Treatment Type Id',
      flex: 1,
      filterable: false
    },
    {
      field: 'treatmentStsId',
      headerName: 'Treatment Sts Id',
      flex: 1,
      filterable: false
    },
    {
      field: 'rte',
      headerName: 'Rte Id',
      renderCell: renderCellExpand,
      flex: 1,
      filterable: false
    },
    {
      field: 'cbpInv',
      headerName: 'Cbplnv Id',
      renderCell: renderCellExpand,
      flex: 1,
      filterable: false
    },
    { field: 'Amount', headerName: 'Amount', flex: 1, filterable: false },
  ];
  return (
    <>
      <ContentTitle container>
        <Grid item xs={4}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <BackIcon />
            <PageHeading title='Treatments' />
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box style={{ alignItems: 'center' }}>
            <Typography variant='h4' align='center'>Treatments</Typography>
          </Box>
        </Grid>
      </ContentTitle>
      <div style={{ height: 670, width: '100%' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          <div style={{ flexGrow: 1 }}>
            <DataGridPro
              //autoHeight
              filterMode="server"
              onFilterModelChange={handleFilterChange}
              //getRowHeight={() => 'auto'}
              columns={columnsData}
              rows={treatmentList.data}
              pageSize={treatmentList.pageSize}
              page={treatmentList.page - 1}
              rowCount={treatmentList.total}
              paginationMode='server'
              sortingMode='server'
              onPageSizeChange={(newPageSize) =>
                setTreatmentList((prveState) => ({
                  ...prveState,
                  pageSize: newPageSize,
                }))
              }
              onPageChange={(newPage) => {
                setTreatmentList((prveState) => ({
                  ...prveState,
                  page: newPage + 1,
                }));
              }}
              rowsPerPageOptions={paginationSize}
              pagination
              components={{ Toolbar: QuickSearchToolbar }}
              experimentalFeatures={{ newEditingApi: true }}
              onSortModelChange={handleSortModelChange}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              loading={treatmentList.isLoading}
            />
        </div>
      </div>
    </div>      
          </>
  );
};
export default TreatmentList;
